/**
 * BoostDialog.jsx
 * Putting money behind a post that already did well.
 *
 * Written to make the total impossible to miss. The mistake this guards
 * against is reading "£5" as the whole cost when it is the daily figure, so
 * the total for the whole run is the largest number on the screen and the
 * daily amount is the small print under it.
 */

import { useEffect, useState } from 'react';
import { api } from '../services/api.js';
import { Check, Warning } from './Icons.jsx';

const money = (pence) => `£${(pence / 100).toFixed(2)}`;

export default function BoostDialog({ brand, post, onClose, onCreated }) {
  const [goals, setGoals] = useState([]);
  const [limits, setLimits] = useState(null);
  const [goal, setGoal] = useState('engagement');
  const [pounds, setPounds] = useState('2.00');
  const [days, setDays] = useState(3);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.ads
      .goals()
      .then((d) => {
        setGoals(d.goals);
        setLimits(d.limits);
      })
      .catch(setError);
  }, []);

  const pence = Math.round(Number(pounds.replace(/[^0-9.]/g, '')) * 100);
  const valid =
    Number.isFinite(pence) &&
    limits &&
    pence >= limits.minDailyBudgetPence &&
    pence <= limits.maxDailyBudgetPence &&
    days >= limits.minDurationDays &&
    days <= limits.maxDurationDays;

  const total = Number.isFinite(pence) ? pence * days : 0;

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.ads.create({
        brand_id: brand.id,
        post_id: post.post_id ?? post.id,
        goal,
        daily_budget_pence: pence,
        duration_days: Number(days),
      });
      onCreated(result);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Boost this post</h2>

        {error && (
          <div className="notice notice--error">
            <Warning />
            <span>{error.message}</span>
          </div>
        )}

        <p className="hint">
          Promotes the post that is already live on {brand.name}&rsquo;s Facebook Page. The ad is
          the post — nothing new is written, and nothing you have not already approved goes out.
        </p>

        <div className="field">
          <label>What should it do?</label>
          <div className="btn-row">
            {goals.map((g) => (
              <button
                key={g.key}
                type="button"
                className={`btn btn--sm ${goal === g.key ? 'btn--primary' : ''}`}
                onClick={() => setGoal(g.key)}
              >
                {goal === g.key ? <Check size={15} /> : null}
                {g.label}
              </button>
            ))}
          </div>
          <p className="hint">{goals.find((g) => g.key === goal)?.hint}</p>
        </div>

        <div className="field field--row">
          <div>
            <label htmlFor="b-budget">Per day</label>
            <input
              id="b-budget"
              value={pounds}
              inputMode="decimal"
              onChange={(e) => setPounds(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="b-days">For how many days</label>
            <input
              id="b-days"
              type="number"
              min={limits?.minDurationDays ?? 1}
              max={limits?.maxDurationDays ?? 14}
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            />
          </div>
        </div>

        {limits && (
          <p className="hint">
            Between {money(limits.minDailyBudgetPence)} and {money(limits.maxDailyBudgetPence)} a
            day, for up to {limits.maxDurationDays} days. The daily limit is set on the Settings
            screen.
          </p>
        )}

        {/* The number that matters, made the hardest to misread. */}
        <div className="boost-total">
          <div className="boost-total__value">{money(total)}</div>
          <div className="boost-total__label">
            total, at most &mdash; {money(Number.isFinite(pence) ? pence : 0)} a day for {days}{' '}
            {days === 1 ? 'day' : 'days'}
          </div>
        </div>

        <div className="notice notice--info">
          <Check />
          <span>
            This creates the boost <strong>paused</strong>. It spends nothing until you start it,
            which is a separate button on the Ads screen.
          </span>
        </div>

        <div className="btn-row" style={{ marginTop: 14 }}>
          <button className="btn btn--primary" onClick={create} disabled={!valid || busy}>
            {busy ? 'Creating…' : 'Create it, paused'}
          </button>
          <button className="btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
